/** Task owner type: patient or care team member. */
export type TaskOwnerType = 'patient' | 'care_team';

/** Canonical list of task category codes. Single source of truth — import this instead of duplicating the list. */
export const TASK_CATEGORY_CODES = [
  'clinical',
  'food',
  'measurement',
  'medication',
  'mind',
  'movement',
  'other',
  'sleep',
] as const;

/** Task category codes used by Liza. */
export type TaskCategoryCode = (typeof TASK_CATEGORY_CODES)[number];

/** Lifestyle-change category codes. */
export const LIFESTYLE_TASK_CATEGORY_CODES = [
  'movement',
  'food',
  'sleep',
  'mind',
  'other',
] as const satisfies TaskCategoryCode[];
export type LifestyleTaskCategoryCode = (typeof LIFESTYLE_TASK_CATEGORY_CODES)[number];

export const MEASUREMENT_TASK_CATEGORY_CODES = ['measurement'] as const satisfies TaskCategoryCode[];
export type MeasurementTaskCategoryCode = (typeof MEASUREMENT_TASK_CATEGORY_CODES)[number];

export const MEDICATION_TASK_CATEGORY_CODES = ['medication'] as const satisfies TaskCategoryCode[];
export type MedicationTaskCategoryCode = (typeof MEDICATION_TASK_CATEGORY_CODES)[number];

/** Care plan category codes. */
export type CarePlanCategoryCode = 'watchlist' | 'vitality';

/**
 * The kinds of item that can live under a care plan topic. A rollup of the finer
 * {@link TaskCategoryCode} set: `measurement` and `medication` map 1:1, every
 * lifestyle category collapses to `lifestyle`, and anything else (incl. `clinical`
 * or an uncategorized task) is a generic `task`. Used by the care-plan UI's
 * add/edit/remove menus and the patientpedia `list_care_plan_topics` tool.
 */
export const CARE_PLAN_ITEM_KINDS = ['measurement', 'medication', 'lifestyle', 'task'] as const;
export type CarePlanItemKind = (typeof CARE_PLAN_ITEM_KINDS)[number];

// --- Communication (Message) types ---

const MESSAGE_SENDER_TYPES = ['patient', 'provider', 'llm', 'llm-proactive'] as const;

/** Sender type for a patientpedia message Communication. */
export type MessageSenderType = (typeof MESSAGE_SENDER_TYPES)[number];

/** Valid sender types for runtime validation. */
export const VALID_SENDER_TYPES: ReadonlySet<string> = new Set(MESSAGE_SENDER_TYPES);

/** Client surfaces a chat message can originate from, recorded on `Communication.medium`. */
export const CHAT_SURFACES = ['patient-web', 'patient-native', 'provider-web'] as const;

/** The app surface a chat request came from; keys the surface-aware capabilities prompt. */
export type ChatSurface = (typeof CHAT_SURFACES)[number];

/** Valid chat surfaces for runtime validation. */
export const VALID_CHAT_SURFACES: ReadonlySet<string> = new Set(CHAT_SURFACES);

const CONVERSATION_TYPES = ['shared', 'provider_private'] as const;

/** Conversation type stored on a root Communication. */
export type ConversationType = (typeof CONVERSATION_TYPES)[number];

/** Valid conversation types for runtime validation. */
export const VALID_CONVERSATION_TYPES: ReadonlySet<string> = new Set(CONVERSATION_TYPES);

/** A single chart item attached to an LLM message. */
export type PatientpediaChartDataItem = {
  type: 'line' | 'bar';
  title: string;
  xAxisLabel: string;
  yAxisLabel: string;
  points: Array<{ x: string; y: number; y2?: number }>;
  seriesLabels?: { y: string; y2: string };
  referenceRange?: { low: number | null; high: number | null; unit: string };
};

/** Array of chart data items attached to an LLM message. */
export type PatientpediaChartsData = PatientpediaChartDataItem[];

// --- Suggested Action (Task) types ---

/** Status of an AI suggested action. */
export type SuggestedActionStatus = 'pending' | 'accepted' | 'rejected' | 'failed' | 'expired';

// --- Task types ---

/** Timing repeat structure for recurring schedules (subset of FHIR Timing.repeat). */
export interface TimingRepeat {
  boundsPeriod?: { start?: Date; end?: Date };
  frequency?: number;
  period?: number;
  periodUnit?: 'h' | 'd' | 'wk' | 'mo';
  dayOfWeek?: string[];
  timeOfDay?: string[];
  when?: string[];
}
